import { useCallback, useMemo } from 'react'
import type { Column, FilterState } from 'types/data-grid'

import { useDataGridParams } from './useDataGridParams'

function matchesFilter(raw: unknown, filter: FilterState): boolean {
  const isPresent = raw != null && String(raw) !== ''
  switch (filter.operator) {
    case 'present':
      return isPresent
    case 'empty':
      return !isPresent
    case 'like':
      if (!filter.value) {
        return true
      }
      return String(raw ?? '')
        .toLowerCase()
        .includes(filter.value.toLowerCase())
  }
}

/**
 * Filter, sort, and paginate a data array according to state from
 * `useDataGridParams`. When `storageKey` is provided the state persists in
 * the URL via nuqs; see that hook's docs for the URL shape.
 *
 * Columns may declare a `getValue` accessor that overrides the default
 * `row[key]` lookup for both sort and filter — useful when the rendered
 * cell derives from multiple fields (e.g. "firstName + lastName").
 */
export function useDataGrid<T>(data: T[], pageSize: number, options?: { storageKey?: string; columns?: Column<T>[] }) {
  const { sort, filters, page, toggleSort, setSort, updateFilter, clearFilter, setPage } = useDataGridParams(
    options?.storageKey,
  )

  const accessors = useMemo(() => {
    const map = new Map<string, (row: T) => unknown>()
    for (const col of options?.columns ?? []) {
      if (col.getValue) {
        map.set(col.key, col.getValue)
      }
    }
    return map
  }, [options?.columns])

  const getColumnValue = useCallback(
    (row: T, key: string): unknown => {
      const accessor = accessors.get(key)
      if (accessor) {
        return accessor(row)
      }
      return (row as Record<string, unknown>)[key]
    },
    [accessors],
  )

  const filtered = useMemo(() => {
    const filterEntries = Object.entries(filters)
    if (filterEntries.length === 0) {
      return data
    }

    return data.filter((row) =>
      filterEntries.every(([key, filter]) => {
        const raw = getColumnValue(row, key)
        return matchesFilter(raw, filter)
      }),
    )
  }, [data, filters, getColumnValue])

  const sorted = useMemo(() => {
    if (!sort) {
      return filtered
    }
    const { column, direction } = sort
    return [...filtered].sort((a, b) => {
      const aVal = getColumnValue(a, column)
      const bVal = getColumnValue(b, column)
      if (aVal == null && bVal == null) {
        return 0
      }
      if (aVal == null) {
        return 1
      }
      if (bVal == null) {
        return -1
      }
      if (aVal < bVal) {
        return direction === 'asc' ? -1 : 1
      }
      if (aVal > bVal) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [filtered, sort, getColumnValue])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage = Math.min(page, totalPages - 1)
  const paged = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize)

  return {
    rows: paged,
    sort,
    filters,
    page: safePage,
    totalPages,
    totalRows: sorted.length,
    toggleSort,
    setSort,
    updateFilter,
    clearFilter,
    setPage,
  }
}
